<!-- 
Dialog for editing a prisms user.

This is a testing tool and won't be available in production.
-->
<template>
  <v-dialog :value="show" persistent content-class="dialog md">
    <v-form ref="form">
      <v-card>
        <v-toolbar color="secondary" flat dark>
          <v-toolbar-title>{{ title }}</v-toolbar-title>
          <v-spacer/>
          <our-user-guide-button topic="administration-users"/>
        </v-toolbar>
        <our-busy-indicator :show="busy"/>
        <v-card-text>
          <v-container ref="dataContainer" grid-list-md pa-0>
            <v-layout wrap>
              <p>
                <strong>Note:</strong> This functionality is for test purposes and will not be available in Production environments.
              </p>
              <v-flex xs12 sm6>
                <v-text-field 
                  :value="user.emailAddress" readonly append-icon="queue"
                  label="Email address" hint="You cannot change the email address. Click the icon to copy to the clipboard."
                  @click:append="addToClipboard"
                />  
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field 
                  v-if="show" :value="user.password"
                  label="New password" autofocus required hint="Leave this blank if you don't want to change the existing password"
                  @input="update('password', $event)" 
                />
              </v-flex>
              <v-flex xs12 sm6>
                <our-date-picker 
                  :value="user.passwordExpiryDate" required 
                  label="Password expires*" @input="update('passwordExpiryDate', $event)"
                />
              </v-flex>
              <v-flex xs12 sm6>
                <our-date-picker :value="user.lastLogonDate" label="Last logon" @input="update('lastLogonDate', $event)"/>
              </v-flex>
              <v-flex xs12 sm6>
                <v-select 
                  :items="lookups.userStatuses" :value="user.status" :rules="[rules.required]"
                  label="Status*" @input="update('status', $event)"
                />
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field 
                  :value="user.failedAttempts" :rules="[rules.integer, positive]"
                  label="Failed attempts" required @input="update('failedAttempts', $event)" 
                />
              </v-flex>
            </v-layout>
          </v-container>
          <our-required-field-indicator/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="busy" flat @click.native="hide">Cancel</v-btn>
          <v-btn 
            ref="save-button" :disabled="busy" flat color="success" 
            type="submit" @click.prevent="save"
          >Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :state-error="state.error" />
  </v-dialog>
</template>

<script>
import * as action from "@/store/prisms-users/action-types";
import * as mutation from "@/store/prisms-users/mutation-types";
import lookupMixins from "@/mixins/lookup.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import { SHOW_NOTIFICATION } from "@/store/notifications/mutation-types";

export default {
  mixins: [lookupMixins, ruleMixins],
  model: {
    prop: "show",
    event: "update:show"
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    userId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      showValidationDialog: false,
      positive: v => (v < 0 ? "Value must be 0 or greater" : true)
    };
  },
  computed: {
    state() {
      return this.$store.state.prismsUsers.edit;
    },
    user() {
      return this.state.data;
    },
    busy() {
      return this.state.loading || this.state.saving;
    },
    title() {
      return !this.state.loading ? `Edit user '${this.user.emailAddress}'` : "Edit user";
    }
  },
  watch: {
    async show(nowShowing) {
      if (nowShowing) {
        this.$refs.form.reset();
        this.$store.dispatch(action.EDIT_GET, this.userId);
      }
    }
  },
  methods: {
    update(field, newValue) {
      this.$store.commit(mutation.EDIT_SET_DATA, { [field]: newValue });
    },
    async save() {
      if (this.$refs.form.validate() && (await this.$store.dispatch(action.EDIT_SAVE))) {
        this.hide();
      } else {
        this.showValidationDialog = true;
      }
    },
    hide() {
      this.$emit("update:show", false);
    },
    addToClipboard() {
      this.$copyText(this.user.emailAddress, this.$refs.dataContainer);
      this.$store.commit(SHOW_NOTIFICATION, "Email address has been copied to the clipboard.");
    }
  }
};
</script>
